/* eslint-disable no-type-assertion/no-type-assertion -- F0Message is a wide structural type; casts pin runtime-known shapes that TS can't infer. */

import { type F0Message } from '@factorialco/f0-react'

export type Turn = Array<F0Message | Array<F0Message>>

export type OrderedPart =
  | { kind: 'text'; text: string }
  | {
      kind: 'tool-call'
      toolCallId: string
      toolName: string
      args: Record<string, unknown>
      complete: boolean
    }

type MaybeAgentName = { agentName?: string }
type MaybeCoagentName = { name?: string }

function isAgentStateMessage(message: F0Message): boolean {
  return (
    message.role === 'assistant' && (message as F0Message & MaybeAgentName).agentName !== undefined
  )
}

function isCoagentPlaceholder(message: F0Message): boolean {
  return (message as F0Message & MaybeCoagentName).name === 'coagent-state-render'
}

function isToolResultMessage(message: F0Message): boolean {
  return message.role === 'tool'
}

export function filterNonRenderableMessages(messages: F0Message[]): F0Message[] {
  return messages.filter((m) => !isCoagentPlaceholder(m) && !isToolResultMessage(m))
}

export function analyzeTurn(
  turnMessages: Turn,
  turnIndex: number,
  turnsCount: number,
  inProgress: boolean
) {
  const isLastTurn = turnIndex === turnsCount - 1
  const turnIsComplete = !(inProgress && isLastTurn)

  const hasVisibleAssistantOutput = turnMessages.some(
    (m) =>
      !Array.isArray(m) &&
      m.role === 'assistant' &&
      (!!m.content ||
        m.toolCalls?.some(
          (tc: { function: { name: string } }) => tc.function.name !== 'orchestratorThinking'
        ))
  )

  const showActivityIndicator =
    !turnIsComplete &&
    hasVisibleAssistantOutput &&
    !Array.isArray(turnMessages[turnMessages.length - 1])

  return { isLastTurn, turnIsComplete, showActivityIndicator }
}

export function convertMessagesToTurns(messages: F0Message[]): Turn[] {
  if (messages.length === 0) return []

  const turns: Turn[] = []
  let thinkingGroup: F0Message[] | null = null

  for (const [i, message] of messages.entries()) {
    if (message.role === 'user') {
      turns.push([message])
      thinkingGroup = null
      continue
    }

    if (turns.length === 0) {
      turns.push([])
      thinkingGroup = null
    }

    const currentTurn = turns[turns.length - 1]!

    if (isAgentStateMessage(message) && thinkingGroup) {
      if (i !== messages.length - 1) {
        const idx = currentTurn.indexOf(thinkingGroup)
        if (idx !== -1) currentTurn.splice(idx, 1)
        currentTurn.push(message, thinkingGroup)
      }
      continue
    }

    if (isThinkingMessage(message)) {
      if (thinkingGroup) {
        const prev = thinkingGroup[thinkingGroup.length - 1]!
        if (getThinkingKey(prev) !== getThinkingKey(message)) {
          thinkingGroup.push(message)
        }
      } else {
        thinkingGroup = [message]
        if (currentTurn.length > 1) {
          currentTurn.splice(1, 0, thinkingGroup)
        } else {
          currentTurn.push(thinkingGroup)
        }
      }
      continue
    }

    currentTurn.push(message)
  }

  return turns
}

export function extractThinkingGroup(turnMessages: Turn): {
  thinkingGroup: F0Message[] | null
  restMessages: Array<F0Message | Array<F0Message>>
} {
  const thinkingGroup = turnMessages.find((m) => Array.isArray(m)) as F0Message[] | undefined
  const restMessages = turnMessages.filter((m) => !Array.isArray(m))
  return { thinkingGroup: thinkingGroup ?? null, restMessages }
}

function isThinkingMessage(message: F0Message): boolean {
  return (
    message.role === 'assistant' &&
    !message.content &&
    message.toolCalls?.some(
      (call: { function: { name: string } }) => call.function.name === 'orchestratorThinking'
    ) === true
  )
}

function getThinkingKey(message: F0Message): string {
  const tc = (
    message as {
      toolCalls?: { function: { name: string; arguments: string } }[]
    }
  ).toolCalls?.find((c) => c.function.name === 'orchestratorThinking')
  return (
    tc?.function.arguments ||
    (typeof message.content === 'string' ? message.content : '') ||
    message.id
  )
}

export function expandFromOrderedParts(msg: F0Message, parts: OrderedPart[]): F0Message[] {
  return parts.map((part, i) => {
    if (part.kind === 'text') {
      return {
        id: `${msg.id}_p${i}_text`,
        role: msg.role,
        content: part.text,
      } as F0Message
    }
    return {
      id: `${msg.id}_p${i}_tc`,
      role: msg.role,
      content: '',
      toolCalls: [
        {
          id: part.toolCallId,
          type: 'function',
          function: {
            name: part.toolName,
            arguments: JSON.stringify(part.args ?? {}),
          },
        },
      ],
    } as F0Message
  })
}

// Splits `msg.toolCalls` and `msg.content` with the "tool calls first,
// text last" ordering the agent assumes when no streaming order was captured.
export function legacyExpansion(msg: F0Message): F0Message[] {
  const toolCalls = (msg as F0Message & { toolCalls?: unknown }).toolCalls as
    | { id: string; function: { name: string; arguments: string } }[]
    | undefined

  if (!toolCalls || toolCalls.length === 0) return [msg]
  if (toolCalls.length === 1 && !msg.content) return [msg]

  const expanded: F0Message[] = []
  for (let i = 0; i < toolCalls.length; i++) {
    expanded.push({
      id: `${msg.id}_tc${i}`,
      role: msg.role,
      content: '',
      toolCalls: [toolCalls[i]],
    } as F0Message)
  }
  if (msg.content) {
    expanded.push({
      id: `${msg.id}_text`,
      role: msg.role,
      content: msg.content,
    } as F0Message)
  }
  return expanded
}
